/** @format — Documents toolbar: search, filters, view toggle */

import { LayoutGrid, List } from "lucide-react";
import { cn } from "@/lib/utils";
import SearchField from "@/components/filters/SearchField";
import FilterDropdown from "@/components/filters/FilterDropdown";
import FilterMenuItem from "@/components/filters/FilterMenuItem";
import FilterMenuPanel from "@/components/filters/FilterMenuPanel";
import DocumentsDateFilterDropdown from "./DocumentsDateFilterDropdown";
import DocumentsSortDropdown from "./DocumentsSortDropdown";
import { DOCUMENT_TYPE_OPTIONS } from "../utils/documentFormatters";
import type {
  CustomDateRange,
  DocumentDateFilter,
  DocumentSortBy,
  DocumentSortOrder,
  DocumentsViewMode,
} from "../types";

function ViewModeToggle({
  viewMode,
  onViewModeChange,
}: {
  viewMode: DocumentsViewMode;
  onViewModeChange: (value: DocumentsViewMode) => void;
}) {
  const buttonBase =
    "m-0 box-border flex h-7 min-h-7 max-h-7 w-7 min-w-7 max-w-7 flex-none items-center justify-center rounded-[6px] border-0 p-0 transition-colors md:h-6 md:min-h-6 md:max-h-6 md:w-6 md:min-w-6 md:max-w-6";
  const activeClass = "bg-white shadow-[0px_1px_2px_0_rgba(0,0,0,0.06)]";
  const inactiveClass = "bg-transparent hover:bg-white/60";

  return (
    <div className="box-border flex h-8 min-h-8 max-h-8 flex-none items-center justify-center gap-0.5 overflow-hidden rounded-lg bg-neutral-100 px-0.5 md:h-7 md:min-h-7 md:max-h-7">
      <button
        type="button"
        aria-label="Grid view"
        aria-pressed={viewMode === "grid"}
        onClick={() => onViewModeChange("grid")}
        className={cn(buttonBase, viewMode === "grid" ? activeClass : inactiveClass)}
      >
        <LayoutGrid className="h-4 w-4 text-[#525252]" strokeWidth={2} />
      </button>
      <button
        type="button"
        aria-label="List view"
        aria-pressed={viewMode === "list"}
        onClick={() => onViewModeChange("list")}
        className={cn(buttonBase, viewMode === "list" ? activeClass : inactiveClass)}
      >
        <List className="h-4 w-4 text-[#525252]" strokeWidth={2} />
      </button>
    </div>
  );
}

function TypeFilterDropdown({
  type,
  onTypeChange,
  typeOptions,
  buttonClassName,
}: {
  type: string;
  onTypeChange: (value: string) => void;
  typeOptions: Array<{ value: string; label: string }>;
  buttonClassName?: string;
}) {
  const typeLabel = type === "all" ? "Document type" : typeOptions.find((o) => o.value === type)?.label;

  return (
    <FilterDropdown
      label="Document type"
      valueLabel={typeLabel}
      compactLabel={type === "all" ? "Type" : typeLabel}
      active={type !== "all"}
      buttonClassName={buttonClassName}
      menuWidth={157}
    >
      {(close) => (
        <FilterMenuPanel width={157}>
          {typeOptions.map((option) => (
            <FilterMenuItem
              key={option.value}
              active={type === option.value}
              onClick={() => {
                onTypeChange(option.value);
                close();
              }}
            >
              {option.label}
            </FilterMenuItem>
          ))}
        </FilterMenuPanel>
      )}
    </FilterDropdown>
  );
}

export default function DocumentsToolbar({
  search,
  onSearchChange,
  type,
  onTypeChange,
  dateFilter,
  customDateRange,
  onDateFilterChange,
  onCustomDateRangeChange,
  sortBy,
  sortOrder,
  onSortByChange,
  onSortOrderChange,
  viewMode,
  onViewModeChange,
  typeOptions = DOCUMENT_TYPE_OPTIONS,
  showViewToggle = true,
  showSearch = true,
}: {
  search: string;
  onSearchChange: (value: string) => void;
  type: string;
  onTypeChange: (value: string) => void;
  dateFilter: DocumentDateFilter | string;
  customDateRange: CustomDateRange;
  onDateFilterChange: (value: DocumentDateFilter | string) => void;
  onCustomDateRangeChange: (range: CustomDateRange) => void;
  sortBy: DocumentSortBy;
  sortOrder: DocumentSortOrder;
  onSortByChange: (value: DocumentSortBy) => void;
  onSortOrderChange: (value: DocumentSortOrder) => void;
  viewMode: DocumentsViewMode;
  onViewModeChange: (value: DocumentsViewMode) => void;
  typeOptions?: Array<{ value: string; label: string }>;
  showViewToggle?: boolean;
  showSearch?: boolean;
}) {
  const filterButtonClass = showSearch
    ? "min-w-0 flex-1 md:flex-none md:shrink-0"
    : "shrink-0";

  const filters = (
    <>
      <TypeFilterDropdown
        type={type}
        onTypeChange={onTypeChange}
        typeOptions={typeOptions}
        buttonClassName={filterButtonClass}
      />
      <DocumentsDateFilterDropdown
        value={dateFilter}
        customRange={customDateRange}
        onChange={onDateFilterChange}
        onCustomRangeChange={onCustomDateRangeChange}
        buttonClassName={filterButtonClass}
      />
      <DocumentsSortDropdown
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortByChange={onSortByChange}
        onSortOrderChange={onSortOrderChange}
        buttonClassName={filterButtonClass}
      />
      {showViewToggle ? (
        <ViewModeToggle viewMode={viewMode} onViewModeChange={onViewModeChange} />
      ) : null}
    </>
  );

  return (
    <div
      className={cn(
        "flex flex-col gap-3 md:h-7 md:flex-row md:items-center md:gap-4",
        showSearch ? "w-full" : "w-full md:w-auto md:shrink-0",
      )}
    >
      {showSearch ? (
        <SearchField
          size="docs"
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          onClear={() => onSearchChange("")}
          clearable
          placeholder="Search documents"
          className="w-full md:flex-1"
        />
      ) : null}

      <div
        className={cn(
          "flex min-w-0 items-center gap-1.5 overflow-x-auto pb-1 md:overflow-visible md:pb-0",
          showSearch ? "w-full md:hidden" : "w-full md:w-auto",
        )}
      >
        {filters}
      </div>

      <div
        className={cn(
          "hidden h-7 shrink-0 items-center gap-1.5 md:flex",
          !showSearch && "md:hidden",
        )}
      >
        {filters}
      </div>
    </div>
  );
}
